import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functionsEu } from '../firebase';
import type { SupportedLanguage } from '../i18n';

const memoryCache = new Map<string, string>();

type TranslateTextResponse = { translatedText?: string };

const translateTextCallable = httpsCallable<
  {
    text: string;
    targetLanguage: SupportedLanguage;
    sourceLanguage?: string;
    context?: string;
    collectionName?: string;
    docId?: string;
    field?: string;
  },
  TranslateTextResponse
>(functionsEu, 'translateTextEu');

export async function translateText({
  text,
  targetLanguage,
  sourceLanguage = 'en',
  context,
  collectionName,
  docId,
  field,
}: {
  text: string;
  targetLanguage: SupportedLanguage;
  sourceLanguage?: string;
  context?: string;
  collectionName?: string;
  docId?: string;
  field?: string;
}) {
  if (!text.trim() || targetLanguage === 'en') return text;
  const key = `${targetLanguage}:${context || 'generic'}:${text}`;
  const cached = memoryCache.get(key);
  if (cached) return cached;
  const result = await translateTextCallable({ text, targetLanguage, sourceLanguage, context, collectionName, docId, field });
  const translatedText = result.data.translatedText?.trim() || text;
  memoryCache.set(key, translatedText);
  return translatedText;
}

export async function getTranslatedText({
  collectionName,
  docId,
  field,
  originalText,
  targetLanguage,
  sourceLanguage = 'en',
  context,
}: {
  collectionName: string;
  docId: string;
  field: string;
  originalText: string;
  targetLanguage: SupportedLanguage;
  sourceLanguage?: string;
  context?: string;
}) {
  if (!originalText?.trim() || targetLanguage === 'en') return originalText;
  const cacheKey = `${collectionName}/${docId}:${field}:${targetLanguage}:${originalText}`;
  const memoryHit = memoryCache.get(cacheKey);
  if (memoryHit) return memoryHit;

  const ref = doc(firestore, collectionName, docId);
  const snap = await getDoc(ref).catch(() => null);
  const cached = snap?.data()?.translations?.[targetLanguage]?.[field];
  if (typeof cached === 'string' && cached.trim()) {
    memoryCache.set(cacheKey, cached);
    return cached;
  }

  try {
    const translatedText = await translateText({
      text: originalText,
      targetLanguage,
      sourceLanguage,
      context,
      collectionName,
      docId,
      field,
    });
    memoryCache.set(cacheKey, translatedText);
    return translatedText;
  } catch {
    return originalText;
  }
}

export async function translateVisibleChatMessages({
  messages,
  targetLanguage,
}: {
  messages: Array<{ id: string; text: string }>;
  targetLanguage: SupportedLanguage;
}) {
  if (targetLanguage === 'en') return {};
  const translatedEntries = await Promise.all(
    messages
      .filter(message => message.text?.trim())
      .map(async message => [
        message.id,
        await translateText({
          text: message.text,
          targetLanguage,
          sourceLanguage: 'auto',
          context: 'private chat message. Preserve meaning, tone, names, emojis, and slang.',
        }).catch(() => message.text),
      ] as const),
  );
  return Object.fromEntries(translatedEntries) as Record<string, string>;
}
